import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent } from "../components/ui/card";
import { Spinner } from "../components/ui/spinner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MINT = "#aaffdc";
const PURPLE = "#c57eff";

export default function Dashboard() {
  const { active } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ["summary", active?.slug],
    queryFn: () => endpoints.summary(active!.slug),
    enabled: !!active,
  });

  if (isLoading) {
    return <div className="space-y-6 pb-12 px-8 pt-8"><Spinner /></div>;
  }
  if (error) {
    return <div className="space-y-6 pb-12 px-8 pt-8"><p className="text-sm text-destructive">{(error as Error).message}</p></div>;
  }
  if (!data) return null;

  // Limit checks. Each "atLimit" flag drives the upgrade banner + colours
  // the StatCard quota when the user is right up against (or over) the cap.
  const releasesAtLimit =
    data.usage.releases.limit !== null && data.usage.releases.used >= data.usage.releases.limit;
  const fansAtLimit =
    data.usage.fans.limit !== null && data.usage.fans.used >= data.usage.fans.limit;
  const djsAtLimit =
    data.usage.djs.limit !== null && data.usage.djs.used >= data.usage.djs.limit;
  const anyAtLimit = releasesAtLimit || fansAtLimit || djsAtLimit;

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div>
        <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>Overview</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">{data.workspace.name} · {data.usage.plan.name} plan</p>
      </div>

      {anyAtLimit && (
        <Card style={{ borderColor: "#ff6b6b33" }}>
          <CardContent className="py-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">
                {[
                  releasesAtLimit && `releases this month (${data.usage.releases.limit})`,
                  fansAtLimit && `fan contacts (${data.usage.fans.limit?.toLocaleString()})`,
                  djsAtLimit && `DJ pool (${data.usage.djs.limit})`,
                ].filter(Boolean).join(" · ")} — limit reached.
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Upgrade to keep adding. Existing data stays put either way.
              </p>
            </div>
            <Link to="/app/billing" className="text-xs whitespace-nowrap underline self-center" style={{ color: PURPLE }}>
              Upgrade plan →
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          label="Releases this month"
          value={data.usage.releases.used}
          max={data.usage.releases.limit ?? undefined}
          color={PURPLE}
        />
        <StatCard
          label="Fans"
          value={data.counts.fans}
          max={data.usage.fans.limit ?? undefined}
          color={PURPLE}
        />
        <StatCard
          label="DJ pool"
          value={data.counts.djs}
          max={data.usage.djs.limit ?? undefined}
          color={PURPLE}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-foreground uppercase" style={{ fontSize: "14px", letterSpacing: "0.02em" }}>Recent releases</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">Latest 5 from your catalog</p>
            </div>
            <Link to="/app/releases" className="text-xs text-muted-foreground hover:text-foreground">View all →</Link>
          </div>

          {data.recentReleases.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-12">
              No releases yet. <Link to="/app/releases" className="text-foreground hover:underline">Create one</Link> to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[52px]"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Artist</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentReleases.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="w-[52px]"><Cover url={r.artwork_url} /></TableCell>
                    <TableCell>
                      <Link to={`/app/releases/${r.slug ?? r.id}`} className="hover:underline">{r.title}</Link>
                    </TableCell>
                    <TableCell><span style={{ color: "#adaaaa" }}>{r.artist || "—"}</span></TableCell>
                    <TableCell><StageBadge stage={r.stage} /></TableCell>
                    <TableCell>
                      {r.release_date ? (
                        <span style={{ backgroundColor: "#262626", color: PURPLE, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
                          {r.release_date}
                        </span>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Cover({ url }: { url: string | null }) {
  return (
    <div
      className="rounded overflow-hidden border border-border bg-muted shrink-0"
      style={{ width: 36, height: 36, minWidth: 36, minHeight: 36 }}
    >
      {url && (
        <img
          src={url}
          alt=""
          className="block w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, max, color }: { label: string; value: number; max?: number; color: string }) {
  const pct = max != null && max > 0 ? Math.min((value / max) * 100, 100) : null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-normal uppercase tracking-wider">
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: color }} />
        {label}
      </div>
      <div className="text-2xl tabular-nums" style={{ color }}>
        {value.toLocaleString()}
        {max != null && (
          <span className="text-sm font-normal text-muted-foreground"> / {max.toLocaleString()}</span>
        )}
      </div>
      {pct != null && (
        <div className="h-1 rounded-full bg-white/5">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
        </div>
      )}
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  if (stage === "promo") {
    return (
      <span style={{ backgroundColor: "#262626", color: MINT, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
        promo
      </span>
    );
  }
  if (stage === "released") {
    return <Badge variant="purple">released</Badge>;
  }
  return <Badge variant="secondary">{stage}</Badge>;
}
